import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faPlus, faWindowClose} from "@fortawesome/free-solid-svg-icons";
import Modal from "./modal";
import React, {useCallback, useEffect, useMemo} from "react";
import Select from "react-select";
import Switch from "react-switch";
import AsyncSelect from "react-select/async";
import FormGroup from "@mui/material/FormGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import {cachedFetch} from "../utils/cached-api-wrapper";
import {isEventDrivenNotificationType} from "../utils/notification-defaults";

const NOTIFICATION_TYPE_OPTIONS = [
    {value: "invoice_volume", labelKey: "notifications.invoice_volume", fallback: "Invoice volume"},
    {
        value: "invoice_approvals",
        labelKey: "notifications.invoice_approvals",
        fallback: "Invoice approvals",
    },
    // {
    //     value: "invoice_initial_approval_request",
    //     labelKey: "notifications.invoice_initial_approval_request",
    //     fallback: "Initial invoice approval request",
    // },
    {
        value: "invoice_pending_approval_reminder",
        labelKey: "notifications.invoice_pending_approval_reminder",
        fallback: "Pending approval reminder",
    },
    {
        value: "invoice_item_detailed_approval_request",
        labelKey: "notifications.invoice_item_detailed_approval_request",
        fallback: "Detailed invoice item approval request",
    },
    {
        value: "plaid_missing_receipt_alert",
        labelKey: "notifications.plaid_missing_receipt_alert",
        fallback: "Missing receipt alert",
    },
];
const NOTIFICATION_TYPES_WITH_CONTENT_CONFIG = ["invoice_volume", "invoice_approvals"];
const DEFAULT_CONFIG_BY_TYPE = {
    invoice_initial_approval_request: {
        delivery_mode: "scheduled",
    },
};
const DEFAULT_CONTENT_BY_TYPE = {
    invoice_volume: ["volume_summary"],
    invoice_approvals: ["pending_approval"],
};
const DEFAULT_FILTER_TYPE = "any";

const cloneList = (list) => (Array.isArray(list) ? [...list] : []);
const toggleListValue = (list, item) =>
    list.includes(item) ? list.filter((value) => value !== item) : [...list, item];
const sanitizeSearchQuery = (value = "") => value.trim().replace(/\//g, "");

const SectionCard = ({children}) => (
    <div>
        <div>{children}</div>
    </div>
);

const FilterRow = ({label, children}) => (
    <div className="mb-3">
        <div className="text-muted text-uppercase small mb-1">{label}</div>
        {children}
    </div>
);

const normalizeNotificationConfig = (notification) => {
    const rawConfig = notification.config || {};
    const defaultConfig = DEFAULT_CONFIG_BY_TYPE[notification.type] || {};
    const defaultContent = DEFAULT_CONTENT_BY_TYPE[notification.type] || [];

    const {
        filters = {},
        measurements: _deprecatedMeasurements,
        period: _deprecatedPeriod,
        ...configWithoutDeprecated
    } = rawConfig;

    return {
        ...defaultConfig,
        ...configWithoutDeprecated,
        content:
            Array.isArray(rawConfig.content) && rawConfig.content.length > 0
                ? [...rawConfig.content]
                : [...defaultContent],
        filters: {
            ...filters,
            type: filters.type ?? DEFAULT_FILTER_TYPE,
            supplier_filter: cloneList(filters.supplier_filter),
            gl_filter: cloneList(filters.gl_filter),
            office_filter: cloneList(filters.office_filter),
            unit_filter: cloneList(filters.unit_filter),
            approver_filter: cloneList(filters.approver_filter),
        },
    };
};

const NotificationsEditForm = ({
                                   data,
                                   cancelNotificationModal,
                                   updatedNotification,
                                   setUpdatedNotification,
                                   handleNotificationSubmit,
                               }) => {
    const safeRead = (key) => {
        try {
            return data?.t?.read(key);
        } catch (error) {
            return undefined;
        }
    };

    const translate = (key, fallback) => safeRead(key) || fallback;

    const notificationConfig = useMemo(
        () => normalizeNotificationConfig(updatedNotification),
        [updatedNotification]
    );

    const updateNotificationConfig = useCallback(
        (updater) => {
            setUpdatedNotification((prev) => ({
                ...prev,
                config: updater(normalizeNotificationConfig(prev)),
            }));
        },
        [setUpdatedNotification]
    );

    const handleContentToggle = useCallback(
        (contentKey) => {
            updateNotificationConfig((config) => ({
                ...config,
                content: toggleListValue(config.content || [], contentKey),
            }));
        },
        [updateNotificationConfig]
    );

    const isTypeWithContentConfig = NOTIFICATION_TYPES_WITH_CONTENT_CONFIG.includes(
        updatedNotification.type
    );
    const isInitialApprovalRequest =
        updatedNotification.type === "invoice_initial_approval_request";
    // An event-driven type is fired by the API from a webhook, never by a timer
    // (XPEN-2886-009), so a schedule chosen here would be stored and then
    // ignored. Hide the picker rather than show a setting that does nothing.
    // Any frequency the form is carrying is dropped on save, in
    // withoutScheduleIfEventDriven, so it never reaches the document.
    const isEventDrivenType = isEventDrivenNotificationType(updatedNotification.type);
    // Types whose audience the API works out for itself. Offering a manual
    // address list for these is worse than offering a schedule: an address typed
    // here looks accepted and is then silently dropped. For the missing-receipt
    // alert the audience is the linked card owners, resolved in
    // c_card_service.notify_missing_receipt_owners, which never reads
    // notification.recipients.
    const hasDynamicRecipients =
        isEventDrivenType ||
        updatedNotification.type === "invoice_pending_approval_reminder" ||
        updatedNotification.type === "invoice_item_detailed_approval_request";

    useEffect(() => {
        if (!isTypeWithContentConfig && !isInitialApprovalRequest) {
            return;
        }

        const config = updatedNotification.config || {};
        const needsNormalization =
            (isTypeWithContentConfig &&
                (!Array.isArray(config.content) ||
                    Object.prototype.hasOwnProperty.call(config, "measurements") ||
                    Object.prototype.hasOwnProperty.call(config, "period"))) ||
            (isInitialApprovalRequest && !config.delivery_mode);

        if (needsNormalization) {
            setUpdatedNotification((prev) => ({
                ...prev,
                config: normalizeNotificationConfig(prev),
            }));
        }
    }, [
        isInitialApprovalRequest,
        isTypeWithContentConfig,
        setUpdatedNotification,
        updatedNotification,
    ]);

    const DAYS_OF_WEEK = [
        {value: "mon", label: translate("day.mon_short", "M")},
        {value: "tue", label: translate("day.tue_short", "T")},
        {value: "wed", label: translate("day.wed_short", "W")},
        {value: "thu", label: translate("day.thu_short", "T")},
        {value: "fri", label: translate("day.fri_short", "F")},
        {value: "sat", label: translate("day.sat_short", "S")},
        {value: "sun", label: translate("day.sun_short", "S")},
    ];

    const timezones = [
        {value: "UTC", label: "UTC"},
        {
            value: "America/Toronto",
            label: data.t.read("timezone.america_toronto"),
        },
        {
            value: "America/Vancouver",
            label: data.t.read("timezone.america_vancouver"),
        },
        {
            value: "America/New_York",
            label: data.t.read("timezone.america_new_york"),
        },
        {
            value: "America/Chicago",
            label: data.t.read("timezone.america_chicago"),
        },
        {
            value: "America/Denver",
            label: data.t.read("timezone.america_denver"),
        },
        {
            value: "America/Los_Angeles",
            label: data.t.read("timezone.america_los_angeles"),
        },
        {
            value: "Europe/London",
            label: data.t.read("timezone.europe_london"),
        },
        {
            value: "Europe/Paris",
            label: data.t.read("timezone.europe_paris"),
        },
        {
            value: "Europe/Berlin",
            label: data.t.read("timezone.europe_berlin"),
        },
        {
            value: "Europe/Rome",
            label: data.t.read("timezone.europe_rome"),
        },
    ];

    const renderFrequencyIntervalFields = () => {
        if (updatedNotification.frequency?.type === "monthly") {
            return (
                <div>
                    <label className="text-muted small text-uppercase d-block mb-1">
                        {data.t.read("on_day")}
                    </label>
                    <input
                        required
                        placeholder={data.t.read("day")}
                        type="number"
                        className="form-control"
                        name="day"
                        min="1"
                        max="31"
                        value={updatedNotification.frequency?.interval?.day_of_the_month ?? ""}
                        onChange={(e) =>
                            setUpdatedNotification({
                                ...updatedNotification,
                                frequency: {
                                    ...updatedNotification.frequency,
                                    interval: {
                                        ...(updatedNotification.frequency?.interval || {}),
                                        day_of_the_month: e.target.value,
                                    },
                                },
                            })
                        }
                    />
                </div>
            );
        }

        if (updatedNotification.frequency?.type === "weekly") {
            return (
                <div>
                    <label className="text-muted small text-uppercase d-block mb-2">
                        {translate("notifications.weekly_days_label", translate("days", "Days"))}
                    </label>
                    <div className="d-flex flex-wrap">
                        {DAYS_OF_WEEK.map((day) => {
                            const isSelected =
                                updatedNotification.frequency?.interval?.day_of_week?.includes(
                                    day.value
                                );
                            const toggleDay = () => {
                                const existing =
                                    updatedNotification.frequency?.interval?.day_of_week || [];
                                const newDays = isSelected
                                    ? existing.filter((d) => d !== day.value)
                                    : [...existing, day.value];
                                setUpdatedNotification({
                                    ...updatedNotification,
                                    frequency: {
                                        ...updatedNotification.frequency,
                                        interval: {
                                            ...(updatedNotification.frequency?.interval || {}),
                                            day_of_week: newDays,
                                        },
                                    },
                                });
                            };
                            return (
                                <button
                                    key={day.value}
                                    type="button"
                                    aria-pressed={isSelected}
                                    onClick={toggleDay}
                                    className={`btn btn-outline-primary btn-sm mr-2 mb-2 day-btn ${
                                        isSelected ? "active" : ""
                                    }`}
                                >
                                    {day.label}
                                </button>
                            );
                        })}
                    </div>
                </div>
            );
        }

        if (updatedNotification.frequency?.type === "yearly") {
            return (
                <div>
                    <label className="text-muted small text-uppercase d-block mb-1">
                        {data.t.read("on_day")}
                    </label>
                    <input
                        required
                        placeholder={data.t.read("day")}
                        type="date"
                        className="form-control"
                        value={updatedNotification.frequency?.interval?.date || ""}
                        onChange={(e) => {
                            const dateStr = e.target.value;
                            const [year, month, day] = dateStr
                                .split("-")
                                .map((n) => parseInt(n, 10));

                            setUpdatedNotification({
                                ...updatedNotification,
                                frequency: {
                                    ...updatedNotification.frequency,
                                    interval: {
                                        ...(updatedNotification.frequency?.interval || {}),
                                        year: year,
                                        month: month,
                                        day_of_month: day,
                                        date: e.target.value,
                                    },
                                },
                            });
                        }}
                    />
                </div>
            );
        }

        return null;
    };

    const styleSelect = {
        control: (baseStyles, state) => ({
            ...baseStyles,
            boxShadow: state.isFocused ? "0px 0px 5px #F39800 " : "0 !important",
            width: "100%",
            borderColor: state.isFocused ? "#F39800" : baseStyles.borderColor,
            "&:hover": {
                borderColor: state.isFocused ? "#F39800" : baseStyles.borderColor,
            },
            backgroundColor: "transparent",
        }),
        menuPortal: (base) => ({
            ...base,
            zIndex: 9999,
        }),
    };

    const supplierMap = (supplier) => {
        if (typeof supplier !== "object" || supplier === null) {
            return null;
        }

        return {
            value: {_id: supplier._id, name: supplier.name, number: supplier.number},
            label: supplier.name,
        };
    };

    const loadSuppliers = (search_query = "", callback = () => {}) => {
        const val = sanitizeSearchQuery(search_query);
        cachedFetch(`/suppliers/search/${val}`, "GET", {}, (response) => {
            const list = Array.isArray(response.data) ? response.data : [];
            const alreadySelectedIds =
                notificationConfig.filters?.supplier_filter?.map(
                    (supplier) => supplier._id
                ) || [];
            const options = list
                .filter((supplier) => !alreadySelectedIds.includes(supplier._id))
                .map(supplierMap)
                .filter(Boolean);

            callback(options);
        });
    };

    const glMap = (gl) => {
        if (typeof gl !== "object" || gl === null) {
            return null;
        }
        return {
            value: {_id: gl._id, name: gl.name},
            label: gl.name,
        };
    };

    const loadGls = (search_query = "", callback = () => {}) => {
        const val = sanitizeSearchQuery(search_query);
        cachedFetch(`/gl/nat/search/${val}`, "GET", {}, (response) => {
            const list = Array.isArray(response.data) ? response.data : [];
            const alreadySelectedIds =
                notificationConfig.filters?.gl_filter?.map((gl) => gl._id) || [];
            const options = list
                .filter((gl) => !alreadySelectedIds.includes(gl._id))
                .map(glMap)
                .filter(Boolean);

            callback(options);
        });
    };

    const officeMap = (office) => {
        if (typeof office !== "object" || office === null) {
            return null;
        }
        return {
            value: {_id: office._id, name: office.name},
            label: office.name,
        };
    };

    const loadOffices = (search_query = "", callback = () => {}) => {
        const val = sanitizeSearchQuery(search_query);
        cachedFetch(`/offices/search/${val}`, "GET", {}, (response) => {
            const list = Array.isArray(response.data) ? response.data : [];
            const alreadySelectedIds =
                notificationConfig.filters?.office_filter?.map(
                    (office) => office._id
                ) || [];
            const options = list
                .filter((office) => !alreadySelectedIds.includes(office._id))
                .map(officeMap)
                .filter(Boolean);

            callback(options);
        });
    };

    // --- Approvers filter (for invoice_approvals) ---
    const approverMap = (approver) => {
        if (typeof approver !== "object" || approver === null) {
            return null;
        }

        const fullName = `${approver.first_name || ""} ${approver.last_name || ""}`.trim();
        const labelBase = fullName || approver.username || approver.email || "(approver)";
        const label = approver.email ? `${labelBase} (${approver.email})` : labelBase;

        return {
            value: {
                id_user: approver.id_user,
                first_name: approver.first_name,
                last_name: approver.last_name,
                email: approver.email,
                username: approver.username,
                role: approver.role,
            },
            label,
        };
    };

    const loadApprovers = (search_query = "", callback = () => {}) => {
        const val = sanitizeSearchQuery(search_query);
        cachedFetch(`/client/users/approvers/search/${val}`, "GET", {}, (response) => {
            const list = Array.isArray(response.data) ? response.data : [];
            const alreadySelectedIds =
                notificationConfig.filters?.approver_filter?.map(
                    (a) => a.id_user
                ) || [];
            const options = list
                .filter((approver) => !alreadySelectedIds.includes(approver.id_user))
                .map(approverMap)
                .filter(Boolean);

            callback(options);
        });
    };

    const shouldShowConfigBlock = isTypeWithContentConfig || isInitialApprovalRequest;

    const nameInputId = "notification-name";
    const descriptionInputId = "notification-description";
    const typeSelectId = "notification-type";

    return (
        <form method="post" onSubmit={handleNotificationSubmit}>
            <Modal
                title={translate("notifications.notification", "Notification")}
                okIsSubmit={true}
                cancelAction={cancelNotificationModal}
                okText={data.t.read("save")}
                width="640px"
                cancelText={data.t.read("cancel")}
            >
                <SectionCard>
                    <div className="form-group mb-4">
                        <label
                            htmlFor={nameInputId}
                            className="text-muted text-uppercase small d-block mb-1"
                        >
                            {data.t.read("name")}
                        </label>
                        <input
                            id={nameInputId}
                            required
                            placeholder={data.t.read("name")}
                            type="text"
                            className="form-control"
                            name="name"
                            value={updatedNotification.name}
                            onChange={(e) =>
                                setUpdatedNotification({
                                    ...updatedNotification,
                                    name: e.target.value,
                                })
                            }
                        />
                    </div>
                    <div className="form-group mb-4">
                        <label
                            htmlFor={descriptionInputId}
                            className="text-muted text-uppercase small d-block mb-1"
                        >
                            {data.t.read("description")}
                        </label>
                        <textarea
                            id={descriptionInputId}
                            required
                            placeholder={data.t.read("description")}
                            className="form-control"
                            name="description"
                            rows={3}
                            value={updatedNotification.description}
                            onChange={(e) =>
                                setUpdatedNotification({
                                    ...updatedNotification,
                                    description: e.target.value,
                                })
                            }
                        />
                    </div>
                    <div className="form-group mb-0">
                        <label
                            htmlFor={typeSelectId}
                            className="text-muted text-uppercase small d-block mb-1"
                        >
                            {translate("type", "Type")}
                        </label>
                        <select
                            id={typeSelectId}
                            className="form-control"
                            value={updatedNotification.type}
                            onChange={(e) =>
                                setUpdatedNotification({
                                    ...updatedNotification,
                                    type: e.target.value,
                                })
                            }
                        >
                            {NOTIFICATION_TYPE_OPTIONS.map((option) => (
                                <option key={option.value} value={option.value}>
                                    {translate(option.labelKey, option.fallback)}
                                </option>
                            ))}
                        </select>
                    </div>
                </SectionCard>

                {shouldShowConfigBlock && (
                    <SectionCard>
                        <h5 className="mt-4 text-muted text-uppercase small d-block mb-1">
                            {translate("notifications.config", "Config")}
                        </h5>

                        {isInitialApprovalRequest && (
                            <div className="pl-3 mb-4">
                                <label className="text-muted small text-uppercase d-block mb-2">
                                    {translate(
                                        "notifications.delivery_mode",
                                        "Delivery mode"
                                    )}
                                </label>
                                <select
                                    className="form-control"
                                    value={notificationConfig.delivery_mode || "scheduled"}
                                    onChange={(e) =>
                                        updateNotificationConfig((config) => ({
                                            ...config,
                                            delivery_mode: e.target.value,
                                        }))
                                    }
                                >
                                    <option value="immediate">
                                        {translate(
                                            "notifications.delivery_mode_immediate",
                                            "Immediate trigger"
                                        )}
                                    </option>
                                    <option value="scheduled">
                                        {translate(
                                            "notifications.delivery_mode_scheduled",
                                            "Scheduled distribution"
                                        )}
                                    </option>
                                </select>
                                <small className="form-text text-muted">
                                    {notificationConfig.delivery_mode === "immediate"
                                        ? translate(
                                            "notifications.delivery_mode_immediate_help",
                                            "Send as soon as an approval request is created."
                                        )
                                        : translate(
                                            "notifications.delivery_mode_scheduled_help",
                                            "Use the notification schedule below to distribute approval requests."
                                        )}
                                </small>
                            </div>
                        )}

                        {isTypeWithContentConfig && (
                            <>
                                <div className="pl-3 mb-4">
                                    <label className="text-muted small text-uppercase d-block mb-2">
                                        {translate("notifications.content", "Content")}
                                    </label>
                                    <FormGroup row={true} style={{gap: "0.5rem"}}>
                                        {updatedNotification.type === "invoice_volume" &&
                                            [
                                                {
                                                    value: "volume_summary",
                                                    label: translate(
                                                        "notifications.volume_summary",
                                                        "Volume Summary"
                                                    ),
                                                },
                                                {
                                                    value: "volume_projection",
                                                    label: translate(
                                                        "notifications.volume_projection",
                                                        "Volume Projection"
                                                    ),
                                                },
                                                {
                                                    value: "supplier_report",
                                                    label: translate(
                                                        "notifications.supplier_report",
                                                        "Supplier Report"
                                                    ),
                                                },
                                                {
                                                    value: "gl_report",
                                                    label: translate(
                                                        "notifications.gl_report",
                                                        "GL Report"
                                                    ),
                                                },
                                            ].map((option) => (
                                                <FormControlLabel
                                                    key={option.value}
                                                    control={
                                                        <Checkbox
                                                            size="small"
                                                            checked={notificationConfig.content?.includes(
                                                                option.value
                                                            )}
                                                            onChange={() =>
                                                                handleContentToggle(option.value)
                                                            }
                                                        />
                                                    }
                                                    label={option.label}
                                                />
                                            ))}
                                        {updatedNotification.type === "invoice_approvals" &&
                                            [
                                                {
                                                    value: "pending_approval",
                                                    label: translate(
                                                        "notifications.pending_approval",
                                                        "Invoices needing approval"
                                                    ),
                                                },
                                                {
                                                    value: "approved",
                                                    label: translate(
                                                        "notifications.approved",
                                                        "Approved invoices"
                                                    ),
                                                },
                                                {
                                                    value: "rejected",
                                                    label: translate(
                                                        "notifications.rejected",
                                                        "Rejected invoices"
                                                    ),
                                                },
                                            ].map((option) => (
                                                <FormControlLabel
                                                    key={option.value}
                                                    control={
                                                        <Checkbox
                                                            size="small"
                                                            checked={notificationConfig.content?.includes(
                                                                option.value
                                                            )}
                                                            onChange={() =>
                                                                handleContentToggle(option.value)
                                                            }
                                                        />
                                                    }
                                                    label={option.label}
                                                />
                                            ))}
                                    </FormGroup>
                                </div>

                                <div className="bg-light rounded p-3">
                                    <label className="text-muted small text-uppercase d-block mb-3">
                                        {translate(
                                            "notifications.filters",
                                            safeRead("notifications.filter") || "Filters"
                                        )}
                                    </label>

                                    <FilterRow label={translate("notifications.suppliers", "Suppliers")}>
                                        <AsyncSelect
                                            styles={styleSelect}
                                            menuPortalTarget={document.body}
                                            classNamePrefix="select"
                                            name="suppliers-select"
                                            placeholder={translate(
                                                "notifications.select_suppliers",
                                                "Select suppliers"
                                            )}
                                            isClearable={true}
                                            cacheOptions
                                            defaultOptions
                                            loadOptions={loadSuppliers}
                                            value={notificationConfig.filters?.supplier_filter?.map(
                                                supplierMap
                                            )}
                                            onChange={(selected) => {
                                                const suppliers = selected
                                                    ? selected.map((s) => s.value)
                                                    : [];

                                                updateNotificationConfig((config) => ({
                                                    ...config,
                                                    filters: {
                                                        ...config.filters,
                                                        supplier_filter: suppliers,
                                                    },
                                                }));
                                            }}
                                            isMulti
                                        />
                                    </FilterRow>

                                    <FilterRow label={translate("notifications.gl_naturals", "GL Accounts")}>
                                        <AsyncSelect
                                            styles={styleSelect}
                                            menuPortalTarget={document.body}
                                            classNamePrefix="select"
                                            name="gls-select"
                                            placeholder={translate(
                                                "notifications.select_gl_naturals",
                                                "Select GL Naturals"
                                            )}
                                            isClearable={true}
                                            cacheOptions
                                            defaultOptions
                                            loadOptions={loadGls}
                                            value={notificationConfig.filters?.gl_filter?.map(
                                                glMap
                                            )}
                                            onChange={(selected) => {
                                                const gls = selected
                                                    ? selected.map((s) => s.value)
                                                    : [];

                                                updateNotificationConfig((config) => ({
                                                    ...config,
                                                    filters: {
                                                        ...config.filters,
                                                        gl_filter: gls,
                                                    },
                                                }));
                                            }}
                                            isMulti
                                        />
                                    </FilterRow>

                                    <FilterRow label={translate("notifications.offices", "Offices")}>
                                        <AsyncSelect
                                            styles={styleSelect}
                                            menuPortalTarget={document.body}
                                            classNamePrefix="select"
                                            name="offices-select"
                                            placeholder={translate(
                                                "notifications.select_offices",
                                                "Select offices"
                                            )}
                                            isClearable={true}
                                            cacheOptions
                                            defaultOptions
                                            loadOptions={loadOffices}
                                            value={notificationConfig.filters?.office_filter?.map(
                                                officeMap
                                            )}
                                            onChange={(selected) => {
                                                const offices = selected
                                                    ? selected.map((s) => s.value)
                                                    : [];
                                                updateNotificationConfig((config) => ({
                                                    ...config,
                                                    filters: {
                                                        ...config.filters,
                                                        office_filter: offices,
                                                    },
                                                }));
                                            }}
                                            isMulti
                                        />
                                    </FilterRow>

                                    {updatedNotification.type === "invoice_approvals" && (
                                        <FilterRow label={translate("notifications.approvers", "Approvers")}>
                                            <AsyncSelect
                                                styles={styleSelect}
                                                menuPortalTarget={document.body}
                                                classNamePrefix="select"
                                                name="approvers-select"
                                                placeholder={translate(
                                                    "notifications.select_approvers",
                                                    "Select approvers"
                                                )}
                                                isClearable={true}
                                                cacheOptions
                                                defaultOptions
                                                loadOptions={loadApprovers}
                                                value={notificationConfig.filters?.approver_filter?.map(
                                                    approverMap
                                                )}
                                                onChange={(selected) => {
                                                    const approvers = selected
                                                        ? selected.map((s) => s.value)
                                                        : [];

                                                    updateNotificationConfig((config) => ({
                                                        ...config,
                                                        filters: {
                                                            ...config.filters,
                                                            approver_filter: approvers,
                                                        },
                                                    }));
                                                }}
                                                isMulti
                                            />
                                        </FilterRow>
                                    )}
                                </div>
                            </>
                        )}
                    </SectionCard>
                )}

                {!isEventDrivenType && (
                    <SectionCard>
                        <h5 className="mb-3">{data.t.read("frequency")}:</h5>
                        <div className="row">
                            <div className="col-md-4 mb-3">
                                <label className="text-muted small text-uppercase d-block mb-1">
                                    {data.t.read("every")}
                                </label>
                                <select
                                    className="form-control"
                                    value={updatedNotification.frequency?.type ?? ""}
                                    onChange={(e) =>
                                        setUpdatedNotification({
                                            ...updatedNotification,
                                            frequency: {
                                                ...updatedNotification.frequency,
                                                type: e.target.value,
                                            },
                                        })
                                    }
                                >
                                    <option value="daily">{data.t.read("daily")}</option>
                                    <option value="weekly">{data.t.read("weekly")}</option>
                                    <option value="monthly">{data.t.read("monthly")}</option>
                                    <option value="yearly">{data.t.read("yearly")}</option>
                                </select>
                            </div>
                            <div className="col-md-8 mb-3">
                                {renderFrequencyIntervalFields()}
                            </div>
                        </div>
                        <div className="row">
                            <div className="col-md-4 mb-3">
                                <label className="text-muted small text-uppercase d-block mb-1">
                                    {data.t.read("time")}
                                </label>
                                <input
                                    required
                                    placeholder={data.t.read("time")}
                                    type="time"
                                    className="form-control"
                                    // ?? "" keeps this a controlled input for a
                                    // document with no stored time, matching
                                    // day_of_the_month above. No test covers it:
                                    // React does not warn here, and the DOM value
                                    // is "" either way, so there is nothing to
                                    // assert -- it is defensive consistency only.
                                    value={updatedNotification.frequency?.interval?.time ?? ""}
                                    onChange={(e) => {
                                        const timeStr = e.target.value;
                                        const [hour, minute] = timeStr
                                            .split(":")
                                            .map((n) => parseInt(n, 10));

                                        setUpdatedNotification({
                                            ...updatedNotification,
                                            frequency: {
                                                ...updatedNotification.frequency,
                                                interval: {
                                                    ...(updatedNotification.frequency?.interval ||
                                                        {}),
                                                    hour: hour,
                                                    minute: minute,
                                                    time: e.target.value,
                                                },
                                            },
                                        });
                                    }}
                                />
                            </div>
                            <div className="col-md-8 mb-3">
                                <label className="text-muted small text-uppercase d-block mb-1">
                                    Timezone
                                </label>
                                <Select
                                    styles={styleSelect}
                                    name="timezone select"
                                    menuPortalTarget={document.body}
                                    value={timezones.find(
                                        (option) =>
                                            option.value ===
                                            updatedNotification.frequency?.interval?.timezone
                                    )}
                                    options={timezones}
                                    classNamePrefix="select"
                                    onChange={(choice) => {
                                        setUpdatedNotification({
                                            ...updatedNotification,
                                            frequency: {
                                                ...updatedNotification.frequency,
                                                interval: {
                                                    ...(updatedNotification.frequency?.interval ||
                                                        {}),
                                                    timezone: choice.value,
                                                },
                                            },
                                        });
                                    }}
                                />
                            </div>
                        </div>
                    </SectionCard>
                )}

                <SectionCard>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <h5 className="mb-0">{data.t.read("recipients")}:</h5>
                        {!hasDynamicRecipients && (
                            <button
                                className="btn btn-primary btn-sm"
                                type="button"
                                onClick={() =>
                                    setUpdatedNotification({
                                        ...updatedNotification,
                                        recipients: [...(updatedNotification.recipients || []), ""],
                                    })
                                }
                            >
                                <FontAwesomeIcon icon={faPlus}/>
                            </button>
                        )}
                    </div>
                    {hasDynamicRecipients ? (
                        <p className="text-muted mb-0">
                            {updatedNotification.type ===
                            "invoice_item_detailed_approval_request"
                                ? translate(
                                      "notifications.dynamic_item_approval_recipients",
                                      "Recipients will be the users who have pending line item approvals."
                                  )
                                : updatedNotification.type ===
                                  "plaid_missing_receipt_alert"
                                ? translate(
                                      "notifications.dynamic_missing_receipt_recipients",
                                      "Recipients will be the owners of the cards with the missing receipts."
                                  )
                                : translate(
                                      "notifications.dynamic_pending_approval_recipients",
                                      "Recipients will be the users who have pending approvals."
                                  )}
                        </p>
                    ) : (
                        (updatedNotification.recipients || []).map((recipient, i) => (
                            <div key={i} className="input-group mb-2">
                                <input
                                    required
                                    placeholder={data.t.read("email")}
                                    type="email"
                                    className="form-control"
                                    name={`recipient-email-${i}`}
                                    value={recipient}
                                    onChange={(e) =>
                                        setUpdatedNotification({
                                            ...updatedNotification,
                                            recipients: (updatedNotification.recipients || []).map(
                                                (item, index) =>
                                                    index === i ? e.target.value : item
                                            ),
                                        })
                                    }
                                />
                                <div className="input-group-append">
                                    <button
                                        type="button"
                                        className="btn btn-link text-danger"
                                        aria-label="Remove recipient"
                                        onClick={() =>
                                            setUpdatedNotification({
                                                ...updatedNotification,
                                                recipients:
                                                    (updatedNotification.recipients || []).filter(
                                                        (x, index) => index !== i
                                                    ),
                                            })
                                        }
                                    >
                                        <FontAwesomeIcon icon={faWindowClose}/>
                                    </button>
                                </div>
                            </div>
                        ))
                    )}
                </SectionCard>

                <SectionCard>
                    <div className="d-flex justify-content-between align-items-center">
                        <div>
                            <h5 className="mb-1">{data.t.read("active")}:</h5>
                            <p className="text-muted small mb-0">
                                Toggle to enable or pause this notification.
                            </p>
                        </div>
                        <Switch
                            className="switch d-block mt-0 ml-2"
                            checked={updatedNotification.active}
                            width={70}
                            onColor="#F39800"
                            onHandleColor="#2693e6"
                            boxShadow="0px 1px 5px rgba(0, 0, 0, 0.6)"
                            activeBoxShadow="0px 0px 1px 10px rgba(0, 0, 0, 0.2)"
                            onChange={(active) =>
                                setUpdatedNotification({
                                    ...updatedNotification,
                                    active: active,
                                })
                            }
                        />
                    </div>
                </SectionCard>
            </Modal>
        </form>
    );
};

export default NotificationsEditForm;
